import {
  authSessionResponseSchema,
  listPasskeysResponseSchema,
  listSessionsResponseSchema,
  loginResponseSchema,
  passkeyAuthenticationOptionsSchema,
  passkeyRegistrationOptionsSchema,
  passkeySchema,
  revokeAllSessionsResponseSchema,
  totpSetupResponseSchema,
  type AuthSessionResponse,
  type LoginRequest,
  type LoginResponse,
  type Passkey,
  type PasskeyAuthenticationOptions,
  type PasskeyAuthenticateVerifyRequest,
  type PasskeyRegisterVerifyRequest,
  type PasskeyRegistrationOptions,
  type RevokeAllSessionsResponse,
  type Session,
  type SignupRequest,
  type TotpDisableRequest,
  type TotpEnableRequest,
  type TotpSetupResponse,
  type TotpVerifyRequest,
} from "@mailflare/shared";

import { apiRequest, setCsrfToken } from "@/lib/api-client";

function adoptSession(session: AuthSessionResponse): AuthSessionResponse {
  setCsrfToken(session.csrfToken);
  return session;
}

export async function getSession(): Promise<AuthSessionResponse> {
  const session = authSessionResponseSchema.parse(await apiRequest<unknown>("/auth/session"));
  return adoptSession(session);
}

export async function signup(body: SignupRequest): Promise<AuthSessionResponse> {
  const session = authSessionResponseSchema.parse(
    await apiRequest<unknown>("/auth/signup", { method: "POST", body }),
  );
  return adoptSession(session);
}

export async function login(body: LoginRequest): Promise<LoginResponse> {
  const result = loginResponseSchema.parse(
    await apiRequest<unknown>("/auth/login", { method: "POST", body }),
  );
  if (result.status === "authenticated") setCsrfToken(result.session.csrfToken);
  return result;
}

export async function verifyTotp(body: TotpVerifyRequest): Promise<AuthSessionResponse> {
  const session = authSessionResponseSchema.parse(
    await apiRequest<unknown>("/auth/totp/verify", { method: "POST", body }),
  );
  return adoptSession(session);
}

export async function logout(): Promise<void> {
  await apiRequest<unknown>("/auth/logout", { method: "POST" });
  setCsrfToken(null);
}

/* -------------------------------- Passkeys -------------------------------- */

export async function passkeyRegistrationOptions(): Promise<PasskeyRegistrationOptions> {
  return passkeyRegistrationOptionsSchema.parse(
    await apiRequest<unknown>("/auth/passkeys/registration/options", { method: "POST" }),
  );
}

export async function passkeyRegistrationVerify(
  body: PasskeyRegisterVerifyRequest,
): Promise<Passkey> {
  return passkeySchema.parse(
    await apiRequest<unknown>("/auth/passkeys/registration/verify", { method: "POST", body }),
  );
}

export async function passkeyAuthenticationOptions(body: {
  readonly email?: string;
}): Promise<PasskeyAuthenticationOptions> {
  return passkeyAuthenticationOptionsSchema.parse(
    await apiRequest<unknown>("/auth/passkeys/authentication/options", { method: "POST", body }),
  );
}

export async function passkeyAuthenticationVerify(
  body: PasskeyAuthenticateVerifyRequest,
): Promise<AuthSessionResponse> {
  const session = authSessionResponseSchema.parse(
    await apiRequest<unknown>("/auth/passkeys/authentication/verify", { method: "POST", body }),
  );
  return adoptSession(session);
}

export async function listPasskeys(): Promise<readonly Passkey[]> {
  return listPasskeysResponseSchema.parse(await apiRequest<unknown>("/auth/passkeys")).passkeys;
}

export async function deletePasskey(id: string): Promise<void> {
  await apiRequest<unknown>(`/auth/passkeys/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/* ---------------------------------- TOTP ---------------------------------- */

export async function totpSetup(): Promise<TotpSetupResponse> {
  return totpSetupResponseSchema.parse(
    await apiRequest<unknown>("/auth/totp/setup", { method: "POST" }),
  );
}

export async function totpEnable(body: TotpEnableRequest): Promise<void> {
  await apiRequest<unknown>("/auth/totp/enable", { method: "POST", body });
}

export async function totpDisable(body: TotpDisableRequest): Promise<void> {
  await apiRequest<unknown>("/auth/totp/disable", { method: "POST", body });
}

/* -------------------------------- Sessions -------------------------------- */

export async function listSessions(): Promise<readonly Session[]> {
  return listSessionsResponseSchema.parse(await apiRequest<unknown>("/auth/sessions")).sessions;
}

export async function revokeSession(id: string): Promise<void> {
  await apiRequest<unknown>(`/auth/sessions/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function revokeAllSessions(): Promise<RevokeAllSessionsResponse> {
  return revokeAllSessionsResponseSchema.parse(
    await apiRequest<unknown>("/auth/sessions/revoke-all", { method: "POST" }),
  );
}
